import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Typography } from "antd";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../Hooks/usePermissions";
import { PERMISSIONS } from "../../Utilis/permissions";
import setupIllustration from "../../assets/company-setup-illustration.png";
import "./style.scss";

type SetupStatus = {
  is_complete?: boolean;
  missing_fields?: string[];
  update_company?: number;
};

const getSetupStatus = (
  company: any,
  userInfo: any
): SetupStatus | null => {
  return (
    company?.setup_status ??
    company?.company_information?.setup_status ??
    userInfo?.setup_status ??
    null
  );
};

export const CompanySetupRequiredModal = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const { hasPermission } = usePermissions();
  const { userInfo, company } = useSelector(
    (state: any) => state.setReducer || {}
  );

  const [dismissed, setDismissed] = useState(false);

  const setupStatus = useMemo(
    () => getSetupStatus(company, userInfo),
    [company, userInfo]
  );

  const isIncomplete = setupStatus?.is_complete === false;
  const canManageSettings = hasPermission(PERMISSIONS.COMPANY.SETTINGS);
  const isOnCompanySettings = pathname.startsWith("/company-setting");

  // Re-open if setup becomes incomplete again after being dismissed while incomplete
  useEffect(() => {
    if (setupStatus?.is_complete === true) {
      setDismissed(false);
    }
  }, [setupStatus?.is_complete]);

  const open =
    !!userInfo &&
    !!company &&
    isIncomplete &&
    canManageSettings &&
    !isOnCompanySettings &&
    !dismissed;

  const handleStart = () => {
    setDismissed(true);
    history.push("/company-setting");
  };

  return (
    <Modal
      className="company-setup-required-modal"
      open={open}
      centered
      width={720}
      footer={null}
      onCancel={() => setDismissed(true)}
      destroyOnClose
    >
      <div className="company-setup-required-modal__content">
        <Typography.Title level={4} className="company-setup-required-modal__title">
          {t("companySettings.setupRequired.title")}
        </Typography.Title>

        <img
          className="company-setup-required-modal__illustration"
          src={setupIllustration}
          alt=""
        />

        <Typography.Paragraph className="company-setup-required-modal__message">
          {t("companySettings.setupRequired.message")}
        </Typography.Paragraph>

        <div className="company-setup-required-modal__actions">
          <Button
            type="primary"
            className="company-setup-required-modal__cta"
            onClick={handleStart}
          >
            {t("companySettings.setupRequired.cta")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CompanySetupRequiredModal;
